import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import {
  CashDrawerCharts,
  CashDrawerSummaryCards,
} from '@/components/reports/CashDrawerReportPresentation'
import {
  CashDrawerSessionDetail,
  CashDrawerSessionsTable,
} from '@/components/reports/CashDrawerSessionsTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import dayjs from '@/lib/dayjs'
import { REPORT_DATE_MODE, getOrganizationReportDateBounds } from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  cashDrawerQueries,
  useCashDrawerLocations,
  useCashDrawerWorkstations,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  reportQueries,
  useCashDrawerReport,
  useExportCashDrawerReportMutation,
} from '@/lib/services/reports/reports.query'
import {
  cashDrawerReportSearchSchema,
  getCashDrawerReportFiltersFromSearch,
  getCashDrawerReportQueryFilters,
  mergeCashDrawerReportFilters,
  type CashDrawerReportFilters,
  type CashDrawerReportSearch,
} from '@/lib/services/reports/reports.schema'
import type { CashDrawerReportSession } from '@/lib/services/cash-drawers/cash-drawers.types'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Moon, Download } from 'lucide-react'
import { startTransition, useState } from 'react'
import {
  getCashDrawerReportSessions,
  getReportBalancingSearch,
} from '@/components/reports/cash-drawer-report.presentation'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { useAbility } from '@/lib/casl'
import { canOperateCashDrawers } from '@/lib/services/money/money.access'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'

export const Route = createFileRoute('/_authenticated/$orgId/reports/cash-drawer/')({
  component: CashDrawerReportPage,
  head: () => adminTitleHead('Cash Drawer Report', 'Reports'),
  validateSearch: (search) => cashDrawerReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    const organizationQuery = organizationQueries.show(params.orgId)
    const organization =
      context.queryClient.getQueryData?.(organizationQuery.queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQuery))
    const filters = getFilters(search, organization?.timezone)
    const queryFilters = getCashDrawerReportQueryFilters(filters)

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      reportQueries.cashDrawer({ organizationId: params.orgId, filters: queryFilters })
    )

    await Promise.all([
      context.queryClient.ensureQueryData(
        cashDrawerQueries.locations({ organizationId: params.orgId })
      ),
      context.queryClient.ensureQueryData(
        cashDrawerQueries.workstations({ organizationId: params.orgId })
      ),
    ])
  },
})

export function CashDrawerReportPage() {
  const [selectedSession, setSelectedSession] = useState<CashDrawerReportSession | null>(null)
  const navigate = useNavigate()
  const path = usePathHelper()
  const ability = useAbility()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getFilters(search, organization?.timezone)
  const queryFilters = getCashDrawerReportQueryFilters(filters)
  const { data: report } = useCashDrawerReport(queryFilters)
  const { data: locations = [] } = useCashDrawerLocations({ organizationId: orgId })
  const { data: workstations = [] } = useCashDrawerWorkstations({ organizationId: orgId })
  const filteredWorkstations = filters.location_id
    ? workstations.filter(
        (workstation) => Number(workstation.location?.id) === Number(filters.location_id)
      )
    : workstations
  const exportMutation = useExportCashDrawerReportMutation()
  const reportNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    reportNow,
    organization?.timezone
  )
  const sessions = getCashDrawerReportSessions(
    report?.sessions ?? [],
    filters,
    organization?.timezone
  )
  const closes = sessions.filter((session) => session.closed_at)
  const canBalance = canOperateCashDrawers(ability, { organization })
  const getBalancingLink = (session: CashDrawerReportSession) => {
    const search = getReportBalancingSearch(session, workstations, canBalance)
    return search
      ? { ...path.cashDrawerBalancing.index().using({ orgId }).link, search }
      : undefined
  }
  const updateFilters = (changes: Partial<CashDrawerReportFilters>) => {
    setSelectedSession(null)
    startTransition(() => {
      void navigate({
        ...path.cashDrawer.index().using({ orgId }).link,
        search: mergeCashDrawerReportFilters(filters, changes),
        replace: true,
      })
    })
  }
  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters: queryFilters,
      })
      downloadExportedReport(
        response,
        `cash-drawer-report-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Unable to export this report right now.'))
    }
  }

  return (
    <div className="max-w-[1180px] space-y-5 pb-6">
      <AdminPageHeader
        icon={Moon}
        title="Cash Drawer Report"
        description="How cash and checks moved through the drawer, every count, and whether it came out right."
        actions={
          <div className="flex flex-wrap items-center gap-3" aria-label="Report controls">
            <ReportDateRangeFilterBar
              appliedDateMode={REPORT_DATE_MODE.CUSTOM}
              initialFilters={{ ...filters, ...datePickerBounds }}
              now={reportNow}
              onApply={({ start_date, end_date, date_mode }) =>
                updateFilters({ start_date, end_date, date_mode })
              }
            />
            <AdminHeaderActionButton
              variant="outline"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              <Download className="size-4" /> Export Spreadsheet
            </AdminHeaderActionButton>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3" aria-label="Report filters">
        <Select
          value={filters.location_id ? String(filters.location_id) : 'all'}
          onValueChange={(value) =>
            updateFilters({
              location_id: value === 'all' ? undefined : Number(value),
              workstation_id: undefined,
            })
          }
        >
          <SelectTrigger
            className="w-full sm:w-56"
            aria-label="Location"
            selected={Boolean(filters.location_id)}
          >
            <SelectValue placeholder="All locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {locations.map((location) => (
              <SelectItem key={location.id} value={String(location.id)}>
                {getLocationName(location)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.workstation_id ? String(filters.workstation_id) : 'all'}
          onValueChange={(value) =>
            updateFilters({ workstation_id: value === 'all' ? undefined : Number(value) })
          }
        >
          <SelectTrigger
            className="w-full sm:w-56"
            aria-label="Register"
            selected={Boolean(filters.workstation_id)}
          >
            <SelectValue placeholder="All registers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All registers</SelectItem>
            {filteredWorkstations.map((workstation) => (
              <SelectItem key={workstation.id} value={String(workstation.id)}>
                {workstation.name}
                {!filters.location_id ? ` · ${workstation.location.name}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <CashDrawerSummaryCards summary={report?.summary} sessions={sessions} />

      <CashDrawerCharts
        sessions={sessions}
        onSelectSession={setSelectedSession}
        timezone={organization?.timezone}
      />

      <Card size="sm">
        <CardHeader size="sm">
          <div className="flex items-center justify-between gap-3">
            <CardTitle size="xs">Counts</CardTitle>
            <Badge variant="secondary">
              {closes.length} {closes.length === 1 ? 'count' : 'counts'}
            </Badge>
          </div>
          <CardDescription>Newest first</CardDescription>
        </CardHeader>
        <CardContent size="sm">
          <CashDrawerSessionsTable
            rows={sessions}
            getBalancingLink={getBalancingLink}
            onSelectSession={setSelectedSession}
            timezone={organization?.timezone}
          />
        </CardContent>
      </Card>

      <CashDrawerSessionDetail
        session={selectedSession}
        timezone={organization?.timezone}
        onClose={() => setSelectedSession(null)}
      />
    </div>
  )
}

function getFilters(search: CashDrawerReportSearch, timezone: string): CashDrawerReportFilters {
  const now = dayjs().tz(timezone)
  return getCashDrawerReportFiltersFromSearch(search, {
    start_date: now.subtract(13, 'day').format('YYYY-MM-DD'),
    end_date: now.format('YYYY-MM-DD'),
    date_mode: REPORT_DATE_MODE.CUSTOM,
  })
}
